import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ReactFlow, {
    addEdge,
    Background,
    Controls,
    Handle,
    MiniMap,
    Position,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
    useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import {Button, Col, Input, Row, Select, Typography} from 'antd';
import {nanoid} from 'nanoid';
import {fetchFlowByMethodId, fetchMethods, fetchPartners, submitFlow} from '../services/api';
import TransformMappingModal from "./TransformMappingModal";
import {useToast} from "../ToastProvider";
import SqlNodeConfigModal from "./SqlNodeConfigModal";
import LogNode from "./LogNode";


const { Title } = Typography;

const BpmnNode = ({ data }) => {
    const borderColor = {
        done: '#52c41a',
        running: '#1890ff',
        error: '#ff4d4f',
    }[data.status] || '#888';

    const backgroundColor = {
        done: '#f6ffed',
        running: '#e6f7ff',
        error: '#fff1f0',
    }[data.status] || '#fff';

    return (
        <div
            style={{
                padding: 10,
                border: `2px solid ${borderColor}`,
                background: backgroundColor,
                borderRadius: 6,
                minWidth: 160,
                textAlign: 'center',
                position: 'relative',
                boxShadow: data.status === 'error' ? '0 0 6px #ff4d4f' : undefined,
            }}
        >
            <Handle type="target" position={Position.Left} style={{ background: '#555' }} />
            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{data.label}</div>
            {data.status && (
                <div style={{ fontSize: 12, fontWeight: 500, color: data.status === 'error' ? '#d32029' : '#096dd9' }}>
                    {data.status === 'error' ? '⚠️ ERROR' : data.status.toUpperCase()}
                </div>
            )}
            {data.result && (
                <pre style={{ fontSize: 10, textAlign: 'left', whiteSpace: 'pre-wrap', color: data.status === 'error' ? 'red' : '#389e0d' }}>
                    {typeof data.result === 'string' ? data.result : JSON.stringify(data.result, null, 1)}
                </pre>
            )}
            {data.label !== 'Stop' && (
                <Handle type="source" position={Position.Right} style={{ background: '#555' }} />
            )}
        </div>
    );
};
const nodeTypes = { bpmn: (nodeProps) => <BpmnNode {...nodeProps} /> };

const FlowDiagramInner = () => {
    const { addToast } = useToast();
    const [partners, setPartners] = useState([]);
    const [methods, setMethods] = useState([]);
    const [schemas, setSchemas] = useState([]);
    const [selectedPartnerId, setSelectedPartnerId] = useState(null);
    const [selectedMethodId, setSelectedMethodId] = useState(null);
    const [flowSummaryStatus, setFlowSummaryStatus] = useState('');
    const levelOptions = ['INFO', 'DEBUG', 'ERROR', 'WARN'];
    const currentMethod = methods.find(m => m.id === selectedMethodId);
    const currentSchema = schemas.find(s => s.id === currentMethod?.schemaId);

    const reactFlowWrapper = useRef(null);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { project } = useReactFlow();
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [editingNode, setEditingNode] = useState(null);
    const getStartIcon = (flowType) => {
        switch (flowType) {
            case 'inbound_push': return '🌐📥 Inbound Push';
            case 'inbound_pull': return '🌐📤 Inbound Pull';
            case 'internal_push': return '🔁📤 Internal Push';
            case 'internal_pull': return '🔁📥 Internal Pull';
            default: return '🔵 Start';
        }
    };
    const [flowType, setFlowType] = useState('inbound_push');
    const defaultDisplayLabels = useMemo(() => ({
        'Start': getStartIcon(flowType),
        'Validate Token': '🔐 Validate Token',
        'sql': '🗄️ SQL Query',
        'Transform': '🔁 Transform',
        'Call API': '🌐 Call API',
        'Push Kafka': '📤 Kafka',
        'Push Queue': '📦 Queue',
        'Send Response': '📨 Send Response',
        'Logs': '📚 Logs',
        'Crawl': 'Crawl',
        'Stop': '🔴 Stop',
    }), [flowType]);
    useEffect(() => {
        setTemplateNodes(prev =>
            prev.map(n => ({
                ...n,
                displayLabel:
                    n.label === 'Start' ? getStartIcon(flowType) : defaultDisplayLabels[n.label] || n.label
            }))
        );
    }, [flowType]);
    const flowTypeOptions = [
        { label: '🔃 VDG pull từ đối tác', value: 'internal_pull' },
        { label: '📤 VDG push đến đối tác', value: 'internal_push' },
        { label: '📥 Đối tác gọi để lấy dữ liệu', value: 'inbound_pull' },
        { label: '📨 Đối tác gọi để gửi dữ liệu', value: 'inbound_push' },
    ];
    const [showSqlModal, setShowSqlModal] = useState(false);
    useEffect(() => { fetchPartners().then((res) => setPartners(res.data)); }, []);
    useEffect(() => {
        if (selectedPartnerId) {
            fetchMethods(selectedPartnerId).then((res) => setMethods(res.data));
            setSelectedMethodId(null);
        }
    }, [selectedPartnerId]);
    useEffect(() => {
        if (selectedMethodId) {
            fetchFlowByMethodId(selectedPartnerId, selectedMethodId).then((res) => {
                const flow = res.data;
                if (!flow || Object.keys(flow).length === 0) {
                    let defaultNodes = [];
                    let defaultEdges = [];

                    if (flowType === 'inbound_pull') {
                        const idStart = nanoid();
                        const idValidate = nanoid();
                        const idSQL = nanoid();
                        const idTransform = nanoid();
                        const idResponse = nanoid();
                        const idStop = nanoid();
                        const idLogs = nanoid();
                        const idCrawl = nanoid();

                        defaultNodes = [
                            {
                                id: idStart,
                                type: 'bpmn',
                                position: { x: 100, y: 100 },
                                data: { label: 'Start', methodId: selectedMethodId },
                            },
                            {
                                id: idValidate,
                                type: 'bpmn',
                                position: { x: 300, y: 100 },
                                data: { label: 'Validate Token', methodId: selectedMethodId },
                            },
                            {
                                id: idSQL,
                                type: 'bpmn',
                                position: { x: 500, y: 100 },
                                data: { label: 'sql', methodId: selectedMethodId },
                            },
                            {
                                id: idTransform,
                                type: 'bpmn',
                                position: { x: 700, y: 100 },
                                data: { label: 'Transform', methodId: selectedMethodId },
                            },
                            {
                                id: idResponse,
                                type: 'bpmn',
                                position: { x: 700, y: 100 },
                                data: { label: 'Send Response', methodId: selectedMethodId },
                            },
                            {
                                id: idLogs,
                                type: 'bpmn',
                                position: { x: 500, y: 100 },
                                data: { label: 'Logs', methodId: selectedMethodId }
                            },
                            {
                                id: idCrawl,
                                type: 'bpmn',
                                position: { x: 500, y: 100 },
                                data: { label: 'Crawl', methodId: selectedMethodId }
                            },
                            {
                                id: idStop,
                                type: 'bpmn',
                                position: { x: 500, y: 100 },
                                data: { label: 'Stop', methodId: selectedMethodId }
                            }
                        ];

                        defaultEdges = [
                            { source: idStart, target: idValidate, label: 'Start → Validate Token' },
                            { source: idValidate, target: idSQL, label: 'Validate Token → SQL' },
                            { source: idSQL, target: idTransform, label: 'SQL → Transform' },
                            { source: idTransform, target: idResponse, label: 'Transform → Send Response' },
                            { source: idResponse, target: idStop, label: 'Send Response → Stop' },
                        ].map(({ source, target, label }) => ({
                            id: `${source}-${target}`,
                            source,
                            target,
                            animated: true,
                            label,
                            style: { stroke: '#999' },
                            labelStyle: { fill: '#555', fontSize: 10 }
                        }));
                    } else {
                        const idStart = nanoid();
                        defaultNodes = [
                            {
                                id: idStart,
                                type: 'bpmn',
                                position: { x: 100, y: 100 },
                                data: { label: 'Start', methodId: selectedMethodId },
                            }
                        ];
                        defaultEdges = [];
                    }
                    setNodes(defaultNodes);
                    setEdges(defaultEdges);
                    setTemplateNodes(templateNodesDefault.map(n => ({
                        ...n,
                        displayLabel: defaultDisplayLabels[n.label] || n.label
                    })));
                    return;
                }
                console.log('flowType:', editingNode?.data?.flowType);
                const reactNodes = flow.nodes.map(n => ({
                    id: n.id,
                    type: 'bpmn',
                    position: n.position,
                    data: {
                        label: n.label,
                        methodId: n.methodId,
                        schemaId: n.schemaId,
                        mapping: n.mapping,
                        sqlConfig: n.sqlConfig
                    }
                }));
                const reactEdges = flow.edges.map(e => {
                    const sourceNode = flow.nodes.find(n => n.id === e.source);
                    const targetNode = flow.nodes.find(n => n.id === e.target);
                    return {
                        id: `${e.source}-${e.target}`,
                        source: e.source,
                        target: e.target,
                        label: `${sourceNode?.label || e.source} → ${targetNode?.label || e.target}`,
                        animated: true,
                        style: { stroke: '#999' },
                        labelStyle: { fill: '#555', fontSize: 10 }
                    };
                });
                setFlowType(flow.flowType);
                setNodes(reactNodes);
                setEdges(reactEdges);
                setTemplateNodes(
                    (flow.templateNodes || []).map(n => ({
                        ...n,
                        displayLabel: defaultDisplayLabels[n.label] || n.label,
                    }))
                );
            });
        }
    }, [selectedPartnerId, selectedMethodId, flowType]);

    const useSafeConnect = (nodes, setEdges, addToast) => useCallback(
        (params) => {
            const sourceNode = nodes.find(n => n.id === params.source);
            if (sourceNode?.data.label === 'Stop') {
                addToast("Không thể nối từ node Stop", "warning");
                return;
            }

            const sourceLabel = sourceNode?.data.label || '';
            const targetLabel = nodes.find(n => n.id === params.target)?.data?.label || '';
            setEdges(eds => addEdge({
                ...params,
                type: 'default',
                label: `${sourceLabel} → ${targetLabel}`,
                labelStyle: { fill: '#555', fontSize: 8 },
                animated: true,
            }, eds));
        },
        [nodes, setEdges, addToast]
    );
    const prepareFlowData = () => ({
        nodes: nodes.map(n => ({ id: n.id, label: n.data.label, methodId: n.data.methodId, schemaId: n.data.schemaId, mapping: n.data.mapping || [],sqlConfig: n.data.sqlConfig || null, position: n.position })),
        edges: edges.map(e => ({ source: e.source, target: e.target })),
        partnerId: selectedPartnerId,
        methodId: selectedMethodId,
        flowType: flowType,
        templateNodes: templateNodes.map(n => ({
            id: n.id,
            label: n.label,
            methodId: n.methodId,
            schemaId: n.schemaId,
        }))
    });

    const handleSubmitFlow = async () => {
        try {
            const flowData = prepareFlowData();
            const res = await submitFlow(flowData);
            const statuses = res.data?.data?.nodeStatuses;
            if (!statuses || Object.keys(statuses).length === 0) return addToast("Thiếu node Start", "error");
            if (statuses.bussiness !== undefined) return addToast(statuses.bussiness.result, "error");

            setNodes(prev => prev.map(node => {
                const update = statuses[node.id];
                return update ? {
                    ...node,
                    data: {
                        ...node.data,
                        status: update.status,
                        result: update.status === 'error' ? update.result || 'Lỗi' : update.result,
                    },
                } : node;
            }));

            const allStatuses = Object.values(statuses);
            const hasError = allStatuses.some(s => s.status === 'error');

            const stopNode = nodes.find(n => n.data.label === 'Stop');
            const stopReached = stopNode && statuses[stopNode.id]?.status;

            let flowSummaryStatus = '';

            if (stopReached) {
                if (hasError) {
                    flowSummaryStatus = '❌ ERROR (đã kết thúc)';
                } else {
                    flowSummaryStatus = '✅ SUCCESS';
                }
            } else {
                flowSummaryStatus = '⚠️ INCOMPLETE';
            }
            setFlowSummaryStatus(flowSummaryStatus);
        } catch (error) {
            const msg = error?.response?.data?.message || "Có lỗi xảy ra khi gửi luồng";
            addToast(msg, "error");
        }
    };

    const handleDragStart = (e, type) => {
        e.dataTransfer.setData('application/reactflow', type);
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDrop = useCallback((e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('application/reactflow');
        if (!type) return;

        const bounds = reactFlowWrapper.current.getBoundingClientRect();
        const position = project({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
        const newNode = {
            id: nanoid(),
            type: 'bpmn',
            position,
            data: { label: type, methodId: currentMethod?.id, schemaName: currentSchema?.name },
        };
        setNodes(nds => nds.concat(newNode));
    }, [project, setNodes, currentMethod, currentSchema]);

    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
    const onConnect =useSafeConnect(nodes, setEdges, addToast);

    const handleExport = () => {
        const flow = prepareFlowData(); // sử dụng đúng cấu trúc đã build
        const blob = new Blob([JSON.stringify(flow, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'flow.json';
        a.click();
        URL.revokeObjectURL(url);
    };
    const [templateNodes, setTemplateNodes] = useState([
        { id: nanoid(), label: 'Start', displayLabel: '🔵 Start' },
        { id: nanoid(), label: 'Validate Token', displayLabel: '🔐 Validate Token' },
        { id: nanoid(), label: 'sql', displayLabel: '🗄️ SQL Query' },
        { id: nanoid(), label: 'Transform', displayLabel: '🔁 Transform' },
        { id: nanoid(), label: 'Call API', displayLabel: '🌐 Call API' },
        { id: nanoid(), label: 'Push Kafka', displayLabel: '📤 Kafka' },
        { id: nanoid(), label: 'Push Queue', displayLabel: '📦 Queue' },
        { id: nanoid(), label: 'Send Response', displayLabel: '📨 Send Response' },
        { id: nanoid(), label: 'Logs', displayLabel: '📚 Logs' },
        { id: nanoid(), label: 'Crawl', displayLabel: '📚 Crawl' },
        { id: nanoid(), label: 'Stop', displayLabel: '🔴 Stop' },
    ]);
    const [templateNodesDefault, setTemplateNodesDefault] = useState([
        { id: nanoid(), label: 'Start', displayLabel: '🔵 Start' },
        { id: nanoid(), label: 'Validate Token', displayLabel: '🔐 Validate Token' },
        { id: nanoid(), label: 'sql', displayLabel: '🗄️ SQL Query' },
        { id: nanoid(), label: 'Transform', displayLabel: '🔁 Transform' },
        { id: nanoid(), label: 'Call API', displayLabel: '🌐 Call API' },
        { id: nanoid(), label: 'Push Kafka', displayLabel: '📤 Kafka' },
        { id: nanoid(), label: 'Push Queue', displayLabel: '📤 Kafka' },
        { id: nanoid(), label: 'Send Response', displayLabel: '📨 Send Response' },
        { id: nanoid(), label: 'Logs', displayLabel: '📚 Logs' },
        { id: nanoid(), label: 'Crawl', displayLabel: '📚 Crawl' },
        { id: nanoid(), label: 'Stop', displayLabel: '🔴 Stop' },
    ]);
    const [newNodeLabel, setNewNodeLabel] = useState('');

    const addNewTemplateNode = () => {
        const trimmed = newNodeLabel.trim();
        if (!trimmed || templateNodes.some(n => n.label === trimmed)) return;
        setTemplateNodes([
            ...templateNodes,
            {
                id: nanoid(),
                label: trimmed,
                displayLabel: trimmed,
            }
        ]);
        setNewNodeLabel('');
    };

    return (
        <>{flowSummaryStatus && (
            <div style={{ marginBottom: 8, fontWeight: 'bold', fontSize: 16, color: flowSummaryStatus.includes('ERROR') ? 'red' : (flowSummaryStatus.includes('SUCCESS') ? 'green' : '#faad14') }}>
                Trạng thái tổng: {flowSummaryStatus}
            </div>
        )}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <label>Chọn Partner</label>
                    <Select style={{ width: '100%' }} placeholder="Chọn Partner" onChange={setSelectedPartnerId} options={partners.map(p => ({ label: p.code, value: p.id }))} value={selectedPartnerId} />
                </Col>
                <Col span={6}>
                    <label>Chọn Method</label>
                    <Select style={{ width: '100%' }} placeholder="Chọn Method" onChange={setSelectedMethodId} options={methods.map(m => ({ label: m.methodName, value: m.id }))} value={selectedMethodId} />
                </Col>
                <Col span={6}>
                    <label>Loại luồng</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Chọn loại luồng"
                        options={flowTypeOptions}
                        value={flowType}
                        onChange={setFlowType}
                    />
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 12 }}>
                <Col span={4}>
                    <Title level={5}>Palette</Title>
                    {templateNodes.map((item) => (
                        <div
                            key={item.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, item.label)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px',
                                marginBottom: '6px',
                                background: '#e6f7ff',
                                border: '1px solid #91d5ff',
                                cursor: 'move',
                                borderRadius: 4,
                            }}
                        >
                            <span>{item.displayLabel || item.label}</span>
                            <Button
                                size="small"
                                type="text"
                                danger
                                onClick={() => setTemplateNodes(templateNodes.filter(n => n.id !== item.id))}
                            >
                                ×
                            </Button>
                        </div>
                    ))}

                    <Input
                        placeholder="Tên node mới"
                        value={newNodeLabel}
                        onChange={(e) => setNewNodeLabel(e.target.value)}
                        onPressEnter={addNewTemplateNode}
                        style={{ marginTop: 8 }}
                    />
                    <Button
                        type="dashed"
                        block
                        style={{ marginTop: 4 }}
                        onClick={addNewTemplateNode}
                    >
                        + Thêm node
                    </Button>
                    <Button onClick={handleExport} style={{ marginTop: 12 }} block>Export JSON</Button>
                    <Button onClick={handleSubmitFlow} type="primary" style={{ marginTop: 8 }} block>Chạy luồng</Button>
                </Col>
                <Col span={20}>
                    <div ref={reactFlowWrapper} style={{ height: 600, border: '1px solid #ddd' }} onDrop={onDrop} onDragOver={onDragOver}>
                        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} nodeTypes={nodeTypes} fitView proOptions={{ capture: true }}
                                   onNodeClick={(_, node) => {
                                       if (node.data.label === 'Transform') {
                                           setEditingNode(node);
                                           setShowMappingModal(true);
                                       }
                                       if (node.data.label === 'sql') {
                                           setEditingNode(node);
                                           setShowSqlModal(true);
                                       }
                                   }
                                   }>
                            <Background />
                            <MiniMap />
                            <Controls />
                        </ReactFlow>
                    </div>
                </Col>
                <TransformMappingModal open={showMappingModal} onCancel={() => setShowMappingModal(false)} initialSchemaId={currentMethod?.schemaId} schemas={schemas} onSave={({ schemaId, fieldMapping }) => {
                    setNodes(nds => nds.map(n => n.id === editingNode.id ? { ...n, data: { ...n.data, schemaId, mapping: fieldMapping } } : n));
                    setShowMappingModal(false);
                }} />
                <SqlNodeConfigModal
                    open={showSqlModal}
                    onCancel={() => setShowSqlModal(false)}
                    initialConfig={{ ...editingNode?.data?.sqlConfig, flowType: flowType }}
                    onSave={(values) => {
                        setNodes(nds => nds.map(n =>
                            n.id === editingNode.id
                                ? {...n, data: {...n.data, sqlConfig: values}}
                                : n
                        ));
                        setShowSqlModal(false);
                    }}
                />
            </Row>
        </>
    );
};

export default function GatewayFlowDiagram() {
    return (
        <ReactFlowProvider>
            <FlowDiagramInner />
        </ReactFlowProvider>
    );
}
